import React, { useCallback, useMemo } from "react";
import DOMPurify from "dompurify";

import _ from "lodash";
import cx from "classnames";

import { BannerView } from "src/components/banner";
import { LearningRecordPodcast } from "src/types/coaching/learners";
import { PodcastContext } from "./context";
import { useTransition, animated, useSpring } from "@react-spring/web";
import { PodcastPlayer } from "./components/player";
import { calculatePercentageRead } from "./components/player/analytics";

import styles from "./view.module.scss";
import { FileService } from "src/services/file.service";
import { PodcastTranscriptsContainer } from "./components/transcripts";
import useMeasure from "react-use-measure";
import { duration } from "moment";
import Button from "src/components/button";
import { useSearchParams } from "react-router-dom";

interface PodcastSummaryComponentProps {
  podcast: LearningRecordPodcast;
}

export const PodcastSummaryComponent: React.FC<PodcastSummaryComponentProps> = ({ podcast }) => {
  const podcastContext = React.useContext(PodcastContext);

  const [searchParams] = useSearchParams();

  const name = useMemo(() => _.get(podcast, "podcast.name") || "", [podcast]);
  const description = useMemo(() => _.get(podcast, "podcast.description") || "", [podcast]);
  const banner = useMemo(() => _.get(podcast, "podcast.banner"), [podcast]);
  const topics = useMemo(() => _.get(podcast, "podcast.topics") || [], [podcast]);
  const status = useMemo(() => _.get(podcast, "status"), [podcast]);

  const autoPlay = useMemo(() => {
    const autoPlayParam = searchParams.get("play");
    return autoPlayParam === "true";
  }, [searchParams]);

  const { segments, duration } = useMemo(() => {
    const audio = _.get(podcast, 'podcast.audio');
    const segments = _.get(audio, 'segments', 1);
    const duration = _.get(audio, 'duration', 0);

    return {
      segments: segments,
      duration: duration
    }
  }, [podcast]);

  const initialTimeSpent = useMemo(() => {
    return _.get(podcast, "session.progress") || 0;
  }, [podcast?.id]);

  const handleBackClicked = () => {
    if (podcastContext.onBack) {
      podcastContext.onBack();
    }
  };

  const handlePodcastPlay = useCallback(() => {
    if (podcast.status === "NOT_STARTED" && podcastContext.startPodcast) {
      podcastContext.startPodcast();
    }
  }, [podcast, podcastContext.startPodcast]);

  const handleAnalyticsChange = useCallback((event: any) => {
    const existingDatapoints = _.get(podcast, "session.dataPoints") || [];
    const datapoints = event.analytics?.datapoints || [];

    let merged = datapoints.map((dp: any) => {
      const existing = _.find(existingDatapoints, { index: dp.index });
      if (existing) {
        return {
          ...existing,
          views: (existing.views || 0) + (dp.views || 0),
          duration: (existing.duration || 0) + (dp.duration || 0),
          completed: existing.completed || dp.completed,
        };
      }

      return dp;
    });

    existingDatapoints.forEach((dp: any) => {
      if (!_.find(merged, { index: dp.index })) {
        merged.push(dp);
      }
    });

    merged = _.orderBy(merged, ["index", "asc"]) as any;
    const percentageCompleted = calculatePercentageRead(duration, segments, merged);

    const updatedDuration = _.reduce(merged, (prev, current: any) => {
      return prev + current.duration;
    }, 0);

    const updatedEvent = {
      ...event,
      analytics: {
        ...event.analytics,
        datapoints: merged,
        percentageRead: percentageCompleted,
        duration: updatedDuration,
      },
    };

    if (podcastContext.onAnalyticsChange) {
      podcastContext.onAnalyticsChange(updatedEvent);
    }
  }, [podcast, podcastContext.onAnalyticsChange]);


  const handleEnrollPodcast = useCallback(() => {
    console.log("handleEnrollPodcast called");
    if (podcastContext.enrollPodcast) {
      podcastContext.enrollPodcast();
    }
  }, [podcast, podcastContext.enrollPodcast]);

  return (
    <div className={styles.podcastContainer}>
      <div className={styles.top}>
        <div className={styles.left}>
          <div className={styles.podcastDetails}>
            {podcastContext.onBack && (
              <div className={styles.back} onClick={handleBackClicked}>
                <i className="paperflite-left-arrow"></i>
                <span>my courses</span>
              </div>
            )}

            <div className={styles.details}>
              <div className={cx(styles.podcastHeader)}>
                <div className={styles.thumbnail}>
                  <BannerView
                    banner={banner as any}
                    defaultBanner="/images/assets/default_asset.jpg"
                  />
                </div>

                <div className={styles.summary}>
                  <div className={styles.name}>{name}</div>
                  <div className={styles.podcastMetadata}>
                    <div className={styles.metadata}>
                      <i className="paperflite-clock" />
                      {FileService.formatDuration(duration, 'long')}
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      Type: Conversational
                    </div>
                  </div>
                  <div className={styles.actions}>
                    {status === "NOT_ENROLLED" && (
                      <Button
                        className={styles.startCourse}
                        text={podcastContext.enrollingPodcast ? "Preparing..." : "Listen Podcast"}
                        variant="gradient"
                        icon="paperflite-play"
                        disabled={podcastContext.enrollingPodcast}
                        onClick={handleEnrollPodcast}
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.description}>
                <p className={styles.title}>Description</p>
                <div
                  className={styles.descriptionContent}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                />
              </div>

              {_.size(topics) > 0 && (
                <div className={styles.topics}>
                  <div className={styles.title}>Topics Covered</div>
                  <div className={styles.topicsContent}>
                    {topics.map((topic: any, index: number) => (
                      <div key={index} className={styles.topic}>
                        <span className={styles.index}>{String(index + 1).padStart(2, '0')}</span>
                        <span className={styles.separator}>/</span>
                        <span className={styles.name}>{topic.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <RightPanel podcast={podcast} />
      </div>
      <div className={styles.bottom}>
        {podcast.podcast && status !== "NOT_ENROLLED" &&
          <PodcastPlayer
            podcast={podcast.podcast}
            initialTimeSpent={initialTimeSpent}
            autoPlay={autoPlay}
            onPlay={handlePodcastPlay}
            onAnalyticsChange={handleAnalyticsChange}
          />
        }
      </div>
    </div>
  );
};

interface LoadingPodcastPageProps {
  onBack?: () => void;
}

export const LoadingPodcastPage: React.FC<LoadingPodcastPageProps> = ({ onBack }) => {
  return (
    <div className={styles.podcastContainer}>
      <div className={styles.top}>
        <div className={styles.left}>
          <div className={styles.podcastDetails}>
            <div
              className={styles.back}
              onClick={() => {
                if (onBack) {
                  onBack();
                }
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>my courses</span>
            </div>
            <div className={cx(styles.loadingContainer, styles.details, styles.placeholder)}>
              <div className={cx(styles.podcastHeader)}>
                <div className={cx(styles.thumbnail, styles.block)}></div>

                <div className={styles.summary}>
                  <div className={cx(styles.name, styles.block)}></div>
                  <div className={styles.podcastMetadata}>
                    <div className={styles.metadata}>
                      <i className="paperflite-clock" />
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                    <span className={styles.border} />
                    <div className={styles.metadata}>
                      <div className={cx(styles.block, styles.label)}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.description}>
                <p className={cx(styles.title, styles.block)}></p>
                <div className={styles.descriptionContent}>
                  <div className={cx(styles.block, styles.line)}></div>
                  <div className={cx(styles.block, styles.line)}></div>
                  <div className={cx(styles.block, styles.line)}></div>
                </div>
              </div>

              <div className={styles.topics}>
                <div className={cx(styles.title, styles.block)}></div>
                <div className={styles.topicsContent}>
                  {_.times(5, (index: number) => (
                    <div key={index} className={styles.topic}>
                      <span className={cx(styles.index, styles.block)} />
                      <span className={styles.separator}>/</span>
                      <span className={cx(styles.name, styles.block)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface RightPanelProps {
  podcast: LearningRecordPodcast;
}

const RightPanel: React.FC<RightPanelProps> = ({ podcast }) => {
  const podcastContext = React.useContext(PodcastContext);

  const transcripts = podcastContext.transcripts;

  const [transcriptExpanded, setTranscriptExpanded] = React.useState(false);
  const skills = useMemo(() => _.get(podcast, "podcast.skills") || [], [podcast]);

  const [panelRef, panelBounds] = useMeasure();
  const [skillsRef, skillsBounds] = useMeasure();

  const [panelStyles] = useSpring({
    transform: transcriptExpanded ? `translateY(-${skillsBounds.height}px)` : 'translateY(0px)',
  }, [transcriptExpanded, skillsBounds]);

  const handleTranscriptExpand = useCallback((value) => {
    setTranscriptExpanded(value);
  }, []);

  return (
    <div ref={panelRef} className={styles.right}>
      <animated.div className={styles.inner} style={panelStyles}>
        <div ref={skillsRef} className={styles.skillsContainer}>
          <p>Skills you will get</p>
          <div className={styles.skills}>
            {_.size(skills) === 0 && (
              <div className={styles.empty}>No skills available</div>
            )}

            {skills.map((skill: any) => {
              return (
                <div key={skill.id} className={styles.skill}>
                  <div className={styles.name}>{skill.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {transcripts &&
          <div className={styles.transcriptsContainer}>
            <PodcastTranscriptsContainer
              transcripts={transcripts}
              expanded={transcriptExpanded}
              height={panelBounds.height}
              onExpand={handleTranscriptExpand}
            />
          </div>
        }
      </animated.div>
    </div>
  );
}